/**
 * Teslyn Cloud's Project settings section "Pre-flight brief" (R161; decision 974.5 and 974.6;
 * docs/specs/safety-brief.md §5; help `cloud.project-setup`), on a project's Set-up tab: the crew check list each
 * new brief on Teslyn Mobile asks (the suggested items chosen, and the project's own items with who ticks them), and
 * whether the project's client sees each brief's PDF under Safety (off by default). Tenant admins, managers and field
 * crew change it; everyone else reads it. The same section is in Teslyn Field's Project settings; both write the
 * project's one `brief_setting` row.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  CHECK_WHO_WORDS,
  CUSTOM_CHECK_PREFIX,
  DEFAULT_BRIEF_SETTING_CHECKS,
  SUGGESTED_CREW_CHECKS,
  briefSelectedChecks,
  canChangeBriefSetting,
  checkBriefSettingChecks,
  type BriefSetting,
  type CheckWho,
  type CrewCheck,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import type { PortalProject } from '../portal/api.ts';
import { loadBriefSetting, saveBriefSetting } from './briefsApi.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const WHO_OPTIONS: ReadonlyArray<readonly [CheckWho, string]> = [
  ['pilot', CHECK_WHO_WORDS.pilot],
  ['operator', CHECK_WHO_WORDS.operator],
  ['all', CHECK_WHO_WORDS.all],
];

const newCustomId = (): string => `${CUSTOM_CHECK_PREFIX}${crypto.randomUUID()}`;

export function BriefSettingPanel({
  sb,
  project,
  roles,
  newId = newCustomId,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
  /** The id of an added item (tests). */
  readonly newId?: () => string;
}): ReactElement | null {
  const [loaded, setLoaded] = useState<BriefSetting | null | 'missing' | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<readonly string[]>(DEFAULT_BRIEF_SETTING_CHECKS.selected);
  const [custom, setCustom] = useState<readonly CrewCheck[]>([]);
  const [clientVisible, setClientVisible] = useState(false);
  const [text, setText] = useState('');
  const [who, setWho] = useState<CheckWho>('all');
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const editable = canChangeBriefSetting(roles) && project.status !== 'archived';

  const reload = useCallback(() => {
    setError(null);
    loadBriefSetting(sb, project.id)
      .then((s) => {
        setLoaded(s);
        if (s && s !== 'missing') {
          setSelected(briefSelectedChecks(s.checks.selected));
          setCustom(s.checks.custom);
          setClientVisible(s.client_visible);
        }
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);

  // the database update with the briefs is not applied yet: nothing to show
  if (loaded === 'missing') return null;
  const title = 'Pre-flight brief';
  if (error)
    return (
      <section className="panel stack" aria-label={title}>
        <h2 className="heading">{title}</h2>
        <ErrorState message={error} onRetry={reload} />
      </section>
    );
  if (loaded === undefined) return <Loading what="Pre-flight brief settings" />;

  const choice = { selected, custom };
  const issues = checkBriefSettingChecks(choice);

  function addItem(): void {
    const t = text.trim();
    if (!t) {
      setNotice({ ok: false, text: 'Type the item first.' });
      return;
    }
    setCustom([...custom, { id: newId(), text: t, who }]);
    setText('');
    setNotice(null);
  }

  async function save(): Promise<void> {
    if (issues.length > 0) {
      setNotice({ ok: false, text: issues.join(' ') });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await saveBriefSetting(sb, project, choice, clientVisible);
      setNotice({
        ok: true,
        text: `Saved. Each new brief on Teslyn Mobile asks these ${selected.length + custom.length} checks; ${
          clientVisible
            ? 'the client sees each brief’s PDF under Safety.'
            : 'the client does not see the briefs.'
        }`,
      });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel stack" aria-label={title}>
      <h2 className="heading">{title}</h2>
      <p className="caption muted">
        The crew checks each pre-flight brief on Teslyn Mobile asks, ticked by each person on their own phone.
        A brief takes the list as it is when the brief starts.{' '}
        {editable
          ? 'Tick the suggested checks to use and add the project’s own.'
          : 'Tenant admins, managers and field crew change it.'}
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      <fieldset className="stack" aria-label="Suggested checks">
        <legend className="label">Suggested checks</legend>
        {SUGGESTED_CREW_CHECKS.map((c) => (
          <CheckField
            key={c.id}
            label={`${c.text} (${CHECK_WHO_WORDS[c.who].toLowerCase()})`}
            checked={selected.includes(c.id)}
            disabled={!editable}
            onChange={(on) => setSelected(on ? [...selected, c.id] : selected.filter((x) => x !== c.id))}
          />
        ))}
      </fieldset>
      <div className="stack" aria-label="The project’s own checks">
        <h3 className="label">The project’s own checks</h3>
        {custom.length === 0 && <p className="body muted">None added.</p>}
        <ul className="stack">
          {custom.map((c) => (
            <li key={c.id} className="row row--wrap">
              <span className="body grow">
                {c.text} <span className="caption muted">({CHECK_WHO_WORDS[c.who].toLowerCase()})</span>
              </span>
              {editable && (
                <Button
                  variant="quiet"
                  aria-label={`Remove: ${c.text}`}
                  onClick={() => setCustom(custom.filter((x) => x.id !== c.id))}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
        {editable && (
          <div className="row row--wrap">
            <TextField label="Add an item" value={text} onChange={setText} />
            <SelectField label="Who ticks it" value={who} onChange={setWho} options={WHO_OPTIONS} />
            <Button variant="secondary" onClick={addItem}>
              Add the item
            </Button>
          </div>
        )}
      </div>
      <CheckField
        label="Clients see each brief’s PDF under Safety"
        checked={clientVisible}
        disabled={!editable}
        onChange={setClientVisible}
      />
      <p className="caption muted">
        Off by default: some mining clients ask for the daily briefs, most do not need them. A brief can name
        a tired pilot or a near miss.
      </p>
      {editable && issues.length > 0 && (
        <p className="notice notice--warn" role="status">
          {issues.join(' ')}
        </p>
      )}
      {editable && (
        <div className="row">
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            Save the pre-flight brief settings
          </Button>
        </div>
      )}
      {loaded && loaded.changed_by_name && (
        <p className="caption muted">Last changed by {loaded.changed_by_name}.</p>
      )}
    </section>
  );
}
